import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { ROUTES } from "config/navConfig";
import NavigationProgressTracker from "components/ui/NavigationProgressTracker";
import StepOrganisationDetails from "./components/StepOrganisationDetails";
import StepInvestorType from "./components/StepInvestorType";
import StepJurisdiction from "./components/StepJurisdiction";
import StepKYCAML from "./components/StepKYCAML";
import StepAccreditation from "./components/StepAccreditation";
import ComplianceSummaryPanel from "./components/ComplianceSummaryPanel";

const STEPS = [
  { label: "Organisation", description: "Legal entity details" },
  { label: "Investor Type", description: "Classification" },
  { label: "Jurisdiction", description: "Regulatory region" },
  { label: "KYC / AML", description: "Document verification" },
  { label: "Accreditation", description: "Qualified investor check" },
];

const DEMO_FORM = {
  organisation: {
    legalName: 'GlobalInfra Partners LLP',
    entityType: 'limited-liability-partnership',
    registrationNumber: 'OC452871',
    addressLine1: '14 Bishopsgate',
    addressLine2: '',
    city: 'London',
    postcode: 'EC2N 4AJ',
    country: 'United Kingdom',
    contactName: 'Marcus Hoffmann',
    contactEmail: 'm.hoffmann@globalinfrapartners.com',
    contactPhone: '+44 20 7946 0012',
  },
  investorType: {
    investorType: 'institutional',
    subType: 'infrastructure-gp',
    aum: '5b-plus',
    investmentFocus: 'Infrastructure equity, greenfield and brownfield',
  },
  jurisdiction: {
    jurisdiction: 'uk-fca',
    regulatoryStatus: 'FCA Authorised — AIFM',
    lei: '549300XTBR9J5HKPF127',
  },
  kyc: {
    attestPep: false,
    attestAml: false,
    attestAccuracy: false,
  },
  accreditation: {},
};

function validateStep(step, formData) {
  const errors = {};
  if (step === 0) {
    const d = formData?.organisation;
    if (!d?.legalName) errors.legalName = "Legal entity name is required";
    if (!d?.entityType) errors.entityType = "Entity type is required";
    if (!d?.registrationNumber) errors.registrationNumber = "Registration number is required";
    if (!d?.addressLine1) errors.addressLine1 = "Address is required";
    if (!d?.city) errors.city = "City is required";
    if (!d?.postcode) errors.postcode = "Postcode is required";
    if (!d?.country) errors.country = "Country is required";
    if (!d?.contactName) errors.contactName = "Contact name is required";
    if (!d?.contactEmail) errors.contactEmail = "Contact email is required";
    if (!d?.contactPhone) errors.contactPhone = "Contact phone is required";
  }
  if (step === 1) {
    if (!formData?.investorType?.investorType) errors.investorType = "Please select an investor type";
    if (!formData?.investorType?.aum) errors.aum = "Please select AUM range";
  }
  if (step === 2) {
    if (!formData?.jurisdiction?.jurisdiction) errors.jurisdiction = "Please select a jurisdiction";
  }
  if (step === 3) {
    const d = formData?.kyc;
    if (!d?.attestPep || !d?.attestAml || !d?.attestAccuracy) {
      errors.attestations = "Please confirm all compliance attestations to proceed";
    }
  }
  return errors;
}

export default function InvestorOnboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(DEMO_FORM);
  const [errors, setErrors] = useState({});
  const [completed, setCompleted] = useState(false);

  const updateStepData = (stepKey) => (data) => {
    setFormData((prev) => ({ ...prev, [stepKey]: data }));
  };

  const stepKeys = ["organisation", "investorType", "jurisdiction", "kyc", "accreditation"];

  const handleNext = () => {
    const stepErrors = validateStep(currentStep, formData);
    if (Object.keys(stepErrors)?.length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    if (currentStep < STEPS?.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      setCompleted(true);
    }
  };

  const handleBack = () => {
    setErrors({});
    if (currentStep > 0) setCurrentStep((prev) => prev - 1);
  };

  const handleStepClick = (index) => {
    if (index <= currentStep) {
      setErrors({});
      setCurrentStep(index);
    }
  };

  if (completed) {
    return <ComplianceSummaryPanel formData={formData} />;
  }

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <StepOrganisationDetails
            data={formData?.organisation}
            onChange={updateStepData("organisation")}
            errors={errors}
          />
        );
      case 1:
        return (
          <StepInvestorType
            data={formData?.investorType}
            onChange={updateStepData("investorType")}
            errors={errors}
          />
        );
      case 2:
        return (
          <StepJurisdiction
            data={formData?.jurisdiction}
            onChange={updateStepData("jurisdiction")}
            errors={errors}
          />
        );
      case 3:
        return (
          <StepKYCAML
            data={formData?.kyc}
            onChange={updateStepData("kyc")}
            errors={errors}
          />
        );
      case 4:
        return (
          <StepAccreditation
            data={formData?.accreditation}
            onChange={updateStepData("accreditation")}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-card border-b border-border px-4 md:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-md bg-amber-100">
            <Icon name="Landmark" size={18} color="var(--color-primary)" />
          </div>
          <div>
            <span className="font-heading font-semibold text-foreground text-base">SeedDrive</span>
            <span className="hidden sm:inline font-caption text-muted-foreground text-xs ml-2">
              Investor Onboarding
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden md:flex items-center gap-1 text-xs font-caption text-muted-foreground">
            <Icon name="Lock" size={12} />
            256-bit encrypted
          </span>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            iconPosition="left"
            iconSize={14}
            onClick={() => navigate(ROUTES.dashboard)}
          >
            Exit
          </Button>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-10">
        {/* Page title */}
        <div className="mb-6 md:mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground mb-1">
            Investor Onboarding & KYC
          </h1>
          <p className="font-caption text-muted-foreground text-sm md:text-base">
            Complete the verification process to access infrastructure equity opportunities on SeedDrive.
          </p>
        </div>

        {/* Progress tracker */}
        <div className="mb-6 md:mb-8 p-4 bg-card rounded-xl border border-border">
          <NavigationProgressTracker
            steps={STEPS}
            currentStep={currentStep}
            onStepClick={handleStepClick}
            orientation="horizontal"
          />
        </div>

        {/* Step content card */}
        <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
          {/* Step header */}
          <div className="px-4 md:px-6 lg:px-8 py-4 md:py-5 border-b border-border bg-muted/30">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-caption font-semibold text-sm flex-shrink-0">
                {currentStep + 1}
              </div>
              <div>
                <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground">
                  {STEPS?.[currentStep]?.label}
                </h2>
                <p className="font-caption text-muted-foreground text-xs md:text-sm">
                  {STEPS?.[currentStep]?.description}
                </p>
              </div>
            </div>
          </div>

          {/* Step body */}
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            {renderStep()}
          </div>

          {/* Navigation footer */}
          <div className="px-4 md:px-6 lg:px-8 py-4 border-t border-border bg-muted/20 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs font-caption text-muted-foreground order-2 sm:order-1">
              <Icon name="Shield" size={12} />
              <span>Your data is encrypted and processed in accordance with GDPR</span>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto order-1 sm:order-2">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  iconName="ChevronLeft"
                  iconPosition="left"
                  iconSize={16}
                  onClick={handleBack}
                  className="flex-1 sm:flex-none"
                >
                  Back
                </Button>
              )}
              <Button
                variant="default"
                iconName={currentStep === STEPS?.length - 1 ? "CheckCircle2" : "ChevronRight"}
                iconPosition="right"
                iconSize={16}
                onClick={handleNext}
                className="flex-1 sm:flex-none"
              >
                {currentStep === STEPS?.length - 1 ? "Complete Onboarding" : "Continue"}
              </Button>
            </div>
          </div>
        </div>

        {/* Help section */}
        <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 rounded-lg border border-border bg-card">
          <div className="flex items-center gap-2 flex-shrink-0">
            <Icon name="HelpCircle" size={16} color="var(--color-secondary)" />
            <span className="font-caption font-semibold text-sm text-foreground">Need assistance?</span>
          </div>
          <p className="text-xs font-caption text-muted-foreground flex-1">
            Our compliance team is available Monday–Friday, 09:00–17:30 GMT.
          </p>
          <div className="flex items-center gap-3">
            <a
              href="mailto:compliance@seedrive.com"
              className="text-xs font-caption text-primary hover:underline flex items-center gap-1"
            >
              <Icon name="Mail" size={12} />
              compliance@seedrive.com
            </a>
            <a
              href="tel:+442079460000"
              className="text-xs font-caption text-primary hover:underline flex items-center gap-1"
            >
              <Icon name="Phone" size={12} />
              +44 20 7946 0000
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}